import { Component, EventListener, findComponents } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import { HorizontalNav } from '../../horizontal-nav/js/horizontal-nav';
// needed so that TS does not remove it as unused import
import '../../horizontal-nav/js/horizontal-nav';

export type FilterCallbackFunction = (
	filterType: string,
	filterItem: {
		itemKey: string;
		itemName: string;
	},
	isSelected: boolean,
	$clickedItem: JQuery<HTMLElement>
) => any | never;

enum States {
	FilterActive = 'state-m-list-filter--active',
	WithSubCategories = 'state-m-list-filter--with-subcategories',
}

// add your selector here, e.g. Element = '.js-m-list-filter',
enum Selectors {
	FilterLink = '.js-a-link--filter',
	FilterButton = '.js-a-button--filter',
	FilterButtonAll = '.js-a-button--filter-all',
	FilterButtonText = '.js-a-button__text',
	SubCategoryList = '.js-m-list-filter__subcategories',
	SubCategoryOutlet = '.js-list-filter__subcategories-outlet',
}

@Component('ListFilter')
export class ListFilter extends GondelJqueryComponent {
	private filterCallbacks: FilterCallbackFunction[] = [];
	private $subCategoryOutlet: JQuery;
	private subCategorySingleSelect: boolean = false;
	private defaultCategory: { itemKey: string; itemName: string } = { itemKey: '', itemName: '' };
	private filteredCategory: { itemKey: string; itemName: string } = { itemKey: '', itemName: '' };
	private subCategoryAllKeyName: string = 'all';

	public start() {
		this.$subCategoryOutlet = this.$ctx.find(Selectors.SubCategoryOutlet);
		this.defaultCategory.itemKey = this.$ctx.data('default-category-key');
		this.defaultCategory.itemName = this.$ctx
			.find(`${Selectors.FilterLink}[data-category-key="${this.defaultCategory.itemKey}"]`)
			.text();
		this.subCategorySingleSelect = this.$ctx.data('sub-category-single-select');
		this.filteredCategory.itemKey = this.$ctx.data('filtered-category-key');
		this.filteredCategory.itemName = this.$ctx
			.find(`${Selectors.FilterLink}[data-category-key="${this.filteredCategory.itemKey}"]`)
			.text();

		// for init use active filter (set in url) or default
		if (this.filteredCategory.itemKey) {
			this.initializeListFilter(this.filteredCategory.itemKey);
		} else {
			this.initializeListFilter(this.defaultCategory.itemKey);
		}
	}

	public sync() {
		const horizontalNavs = findComponents<HorizontalNav>(this._ctx, 'HorizontalNav');
		for (const horizontalNav of horizontalNavs) {
			horizontalNav.initializeScrollBehaviour();
		}
	}

	public getDefaultCategoryKey(): { itemKey: string; itemName: string } {
		return this.defaultCategory;
	}

	public getFilteredCategoryKey(): { itemKey: string; itemName: string } {
		return this.filteredCategory;
	}

	public getSubCategoryAllText(): string {
		let subCategoryAllText = '';
		const $activeSubCategoryAllButton = this.$ctx
			.find(`${Selectors.FilterButtonAll}`)
			.filter(`.${States.FilterActive}`);

		if ($activeSubCategoryAllButton) {
			subCategoryAllText = $activeSubCategoryAllButton
				.eq(0)
				.find(`${Selectors.FilterButtonText}`)
				.text();
		}
		return subCategoryAllText;
	}

	public registerCallback(fnCallback: FilterCallbackFunction) {
		this.filterCallbacks.push(fnCallback);
	}

	public removeCallback(fnCallback: FilterCallbackFunction) {
		const index = this.filterCallbacks.indexOf(fnCallback);

		if (index >= 0) {
			this.filterCallbacks.splice(index, 1);
		}
	}

	@EventListener('click', Selectors.FilterLink)
	public _handleCategoryFilterClick(
		ev: JQuery.ClickEvent<HTMLDivElement, any, HTMLAnchorElement | HTMLButtonElement>
	) {
		ev.preventDefault();
		const $clickedItem = $(ev.currentTarget!);
		const $subCategoryFilterAll = this.$subCategoryOutlet.find(Selectors.FilterButtonAll);
		const selectedCategoryKey = $clickedItem.data('category-key');
		const selectedCategoryName = $clickedItem.text();

		$(Selectors.FilterLink, this.$ctx)
			.not($clickedItem)
			.removeClass(States.FilterActive);

		// toggle the active state on the filter link
		this.toggleActiveClass($clickedItem, true);

		// show the sub categories for the selected category
		this.showSubCategoriesForCategory($clickedItem);

		// set active state for 'all' subcat filter
		$subCategoryFilterAll.toggleClass(States.FilterActive, true);

		// send selected category to handler
		for (const fnCallback of this.filterCallbacks) {
			fnCallback(
				'category',
				{ itemKey: selectedCategoryKey, itemName: selectedCategoryName },
				true,
				$clickedItem
			);
		}
	}

	@EventListener('click', Selectors.FilterButton)
	public _handleSubCategoryFilterClick(
		ev: JQuery.ClickEvent<HTMLDivElement, any, HTMLAnchorElement | HTMLButtonElement>
	) {
		const $clickedItem = $(ev.currentTarget!);
		const $subCategoryFilterAll = this.$subCategoryOutlet.find(Selectors.FilterButtonAll);
		const selectedSubCategoryKey = $clickedItem.data('subcategory-key');
		const selectedSubCategoryName = $clickedItem.text().trim();

		let isSelected = false;
		if (this.subCategorySingleSelect) {
			$(Selectors.FilterButton, this.$ctx)
				.not($clickedItem)
				.removeClass(States.FilterActive);
			this.toggleActiveClass($clickedItem, true);
		} else {
			this.toggleActiveClass($clickedItem);
		}
		if ($clickedItem.hasClass(States.FilterActive)) {
			isSelected = true;

			// remove active state for 'all' filter when any other subcat filter is activated
			if ($clickedItem.not(Selectors.FilterButtonAll)) {
				$subCategoryFilterAll.toggleClass(States.FilterActive, false);
			}
		} else {
			// check if any other filter is still active, if not set active for 'all' subcat filter
			const $filters = this.$subCategoryOutlet.find(Selectors.FilterButton);
			const $activeFilters = $filters.filter(`.${States.FilterActive}`);
			const countActiveFilters = $activeFilters.length;

			if (countActiveFilters === 0) {
				$subCategoryFilterAll.toggleClass(States.FilterActive, true);
			}
		}
		// send selected subcategory to handler
		for (const fnCallback of this.filterCallbacks) {
			fnCallback(
				'subcategory',
				{ itemKey: selectedSubCategoryKey, itemName: selectedSubCategoryName },
				isSelected,
				$clickedItem
			);
		}
	}

	// exclusive to PurposeHub: with each (de)selection special treatment for 'all' subcat filter
	@EventListener('click', Selectors.FilterButtonAll)
	public _handleSubCategoryFilterAllClick(
		ev: JQuery.ClickEvent<HTMLDivElement, any, HTMLAnchorElement | HTMLButtonElement>
	) {
		const $clickedItem = $(ev.currentTarget!);
		const selectedSubCategoryKey = $clickedItem.data('subcategory-key');
		const selectedSubCategoryName = $clickedItem.text().trim();

		let isSelected = false;
		// unselect all other filters
		$(Selectors.FilterButton, this.$ctx)
			.not($clickedItem)
			.removeClass(States.FilterActive);

		if ($clickedItem.hasClass(States.FilterActive)) {
			// if active 'all' clicked again
			ev.preventDefault();
			return;
		} else {
			this.toggleActiveClass($clickedItem, true);
			isSelected = true;

			// send selected subcategory to handler
			for (const fnCallback of this.filterCallbacks) {
				fnCallback(
					'subcategory',
					{ itemKey: selectedSubCategoryKey, itemName: selectedSubCategoryName },
					isSelected,
					$clickedItem
				);
			}
		}
	}

	public setFilterState(selectedCategory: string, selectedSubCategories: string[]) {
		// remove possible active states
		this.toggleActiveClass(this.$ctx.find(Selectors.FilterLink), false);
		this.toggleActiveClass(this.$ctx.find(Selectors.FilterButton), false);

		this.initializeListFilter(selectedCategory);

		$.each(selectedSubCategories, (index, selectedSubCategory) => {
			let $link: JQuery<HTMLElement>;

			if (selectedSubCategory === this.subCategoryAllKeyName) {
				$link = this.$ctx.find(
					`${Selectors.FilterButtonAll}[data-subcategory-key="${this.subCategoryAllKeyName}"]`
				);
			} else {
				$link = this.$ctx.find(`${Selectors.FilterButton}[data-subcategory-key="${selectedSubCategory}"]`);
			}

			this.toggleActiveClass($link, true);
		});
	}

	private initializeListFilter(selectedCategoryKey: string) {
		if (selectedCategoryKey !== '') {
			const $selectedCategoryLink = this.$ctx.find(
				`${Selectors.FilterLink}[data-category-key="${selectedCategoryKey}"]`
			);

			this.showSubCategoriesForCategory($selectedCategoryLink);
			this.toggleActiveClass($selectedCategoryLink, true);
		}
	}

	private toggleActiveClass($item: JQuery, forceState?: boolean) {
		if (forceState !== undefined) {
			$item.toggleClass(States.FilterActive, forceState);
		} else {
			$item.toggleClass(States.FilterActive);
		}
	}

	private showSubCategoriesForCategory($categoryLink: JQuery) {
		const $subCategoryList = $categoryLink.siblings(Selectors.SubCategoryList);
		this.$subCategoryOutlet.empty();

		if ($subCategoryList.length > 0) {
			this.$subCategoryOutlet.append($subCategoryList.clone());
			this.$ctx.addClass(States.WithSubCategories);
		} else {
			this.$ctx.removeClass(States.WithSubCategories);
		}
	}
}
